import type { Product } from '@/features/products/types/products.types';
import { formatDateOnlyShort } from '@/app/utils/formatDate';
import { isValidPaymentSelection, type PaymentSelection } from '../types/schedule.types';
import { isUnusableEnteredDeposit, resolveAppliedDeposit, type AppliedDepositAnomaly } from '../services/appliedDeposit';
import {
    resolveFirstCollectionDate,
    type FirstCollectionAnomaly,
} from '../services/firstCollectionDate';
import { PAID_IN_FULL_METHOD_KEY } from '../services/schedulePreview';
import { formatMinorAmount } from './formatScheduleCurrency';
import { confirmedInstalmentRows, groupEqualPaymentRuns } from './instalmentRuns';
import { schedulingMethodLabel } from './schedulingMethodLabel';

export interface InstalmentRunView {
    readonly label: string;
    readonly amount: string;
}

export type QuoteSummaryPaymentView =
    | { readonly kind: 'paid-in-full'; readonly amount: string }
    | {
          readonly kind: 'schedule';
          readonly methodLabel: string;
          readonly deposit: string;
          readonly instalments: readonly InstalmentRunView[];
          readonly firstCollection: string;
          readonly depositAnomaly?: AppliedDepositAnomaly;
          readonly firstCollectionAnomaly?: FirstCollectionAnomaly;
      };

// Deposit amounts are integer minor units: format with formatMinorAmount, never formatCurrency.
// 'Not selected' is a defensive guard; the journey gates this card on a valid selection.
export const resolveDepositDisplay = (
    paymentSelection: PaymentSelection | undefined,
    currency: string,
): { text: string; anomaly: AppliedDepositAnomaly | undefined } => {
    if (!isValidPaymentSelection(paymentSelection)) {
        return { text: 'Not selected', anomaly: undefined };
    }

    const appliedDeposit = resolveAppliedDeposit(paymentSelection);

    if (appliedDeposit.kind === 'none') {
        return { text: 'None', anomaly: undefined };
    }
    if (appliedDeposit.kind === 'amount') {
        return {
            text: formatMinorAmount(appliedDeposit.amountMinor, currency),
            anomaly: appliedDeposit.anomaly,
        };
    }
    if (isUnusableEnteredDeposit(appliedDeposit)) {
        return { text: 'Entered for this quote (amount unavailable)', anomaly: appliedDeposit.anomaly };
    }
    return { text: 'Set by the payment schedule', anomaly: appliedDeposit.anomaly };
};

// The estimate qualifier lives inside the value so the date can never render unqualified.
// Any failure degrades to prose: elemis re-anchors the real dates at application time.
export const resolveFirstCollectionDisplay = (
    paymentSelection: PaymentSelection | undefined,
): { text: string; anomaly: FirstCollectionAnomaly | undefined } => {
    if (!isValidPaymentSelection(paymentSelection)) {
        return { text: 'Set when the plan is submitted', anomaly: undefined };
    }

    const resolved = resolveFirstCollectionDate(paymentSelection);

    if (resolved.kind === 'unknown') {
        return { text: 'Set when the plan is submitted', anomaly: resolved.anomaly };
    }
    return {
        text: `${formatDateOnlyShort(resolved.date)} (estimated, as quoted ${formatDateOnlyShort(resolved.quotedOn)})`,
        anomaly: undefined,
    };
};

const instalmentViews = (
    selection: PaymentSelection | undefined,
    currency: string,
): InstalmentRunView[] => {
    if (!isValidPaymentSelection(selection)) {
        return [];
    }
    const rows = confirmedInstalmentRows(selection, resolveAppliedDeposit(selection));
    if (rows === null) {
        return [];
    }
    return groupEqualPaymentRuns(rows).map(({ count, paymentMinor }) => ({
        label: `${count} payment${count === 1 ? '' : 's'} of`,
        amount: formatMinorAmount(paymentMinor, currency),
    }));
};

export const buildQuoteSummaryPayment = (
    selection: PaymentSelection | undefined,
    schedulingMethods: Product['schedulingMethods'],
    currency: string,
): QuoteSummaryPaymentView => {
    if (isValidPaymentSelection(selection) && selection.methodKey === PAID_IN_FULL_METHOD_KEY) {
        return { kind: 'paid-in-full', amount: formatMinorAmount(selection.params.amount, currency) };
    }

    // Labelled from the product's schedulingMethods; payment.scheduleType is never written.
    const methodLabel = isValidPaymentSelection(selection)
        ? schedulingMethodLabel(schedulingMethods, selection.methodKey)
        : 'Not selected';
    const deposit = resolveDepositDisplay(selection, currency);
    const firstCollection = resolveFirstCollectionDisplay(selection);

    return {
        kind: 'schedule',
        methodLabel,
        deposit: deposit.text,
        instalments: instalmentViews(selection, currency),
        firstCollection: firstCollection.text,
        depositAnomaly: deposit.anomaly,
        firstCollectionAnomaly: firstCollection.anomaly,
    };
};
